import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { BOARD_TYPES, BOARD_TYPE_LABELS } from '~/consts/ratePlans';

import {
  useApproveChainDraftRatePlans,
  useGetChainDraftRatePlans,
  usePreviewChainDraftRatePlans,
  useSaveChainDraftRatePlans,
} from '~/hooks/apis/reservation/useChainRatePlans';
import type { ChainRatePlansFilters } from '~/hooks/apis/reservation/useChainRatePlans';

import type { ChainDraftRatePlan, ChainDraftRatePlanChanges } from '~/services/ReservationService';

import {
  CANCELLATION_POLICY_OPTIONS,
  applyBulkDraftRatePlanChanges,
  applyDraftRatePlanBatchResult,
  buildDraftRatePlanUpdates,
  clearDraftRatePlanRowError,
  discardDraftRatePlanEdit,
  getApprovableDraftRatePlanIds,
  getDraftApprovalError,
  getUnavailableDraftRatePlanIds,
  isDraftRatePlanRefreshRequired,
  mergeSavedDraftRatePlan,
  reconcileDraftRatePlanRefresh,
  reconcileKnownDraftRatePlans,
  removeUnavailableDraftRatePlanSelections,
  restoreLatestDraftRatePlan,
  updateDraftReviewEdit,
  updateDraftReviewFinancialEdit,
} from './draftRatePlanReviewState';
import type { DraftRatePlanReviewState } from './draftRatePlanReviewState';

interface Props {
  chainSfid: string;
  filters: Omit<ChainRatePlansFilters, 'status'>;
  onPageChange: (page: number) => void;
}

const EMPTY_DRAFT_PLANS: Array<ChainDraftRatePlan> = [];

const fieldLabelSx = { fontSize: 12, minWidth: 140 } as const;
function requestFailureMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

export default function DraftRatePlanReview({ chainSfid, filters, onPageChange }: Props) {
  const query = useGetChainDraftRatePlans(chainSfid, filters);
  const previewMutation = usePreviewChainDraftRatePlans();
  const saveMutation = useSaveChainDraftRatePlans();
  const approveMutation = useApproveChainDraftRatePlans();
  const [reviewState, setReviewState] = useState<DraftRatePlanReviewState>({
    edits: {},
    previewRows: {},
    rowErrors: {},
    conflicts: {},
  });
  const { edits, previewRows, rowErrors, conflicts } = reviewState;
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [knownPlans, setKnownPlans] = useState<Record<string, ChainDraftRatePlan>>({});
  const [previewOpen, setPreviewOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<'save' | 'approve' | null>(null);
  const [notice, setNotice] = useState<{ severity: 'success' | 'warning' | 'error'; text: string } | null>(null);
  const [bulkBoardEnabled, setBulkBoardEnabled] = useState(false);
  const [bulkBoard, setBulkBoard] = useState('breakfast');
  const [bulkPolicyEnabled, setBulkPolicyEnabled] = useState(false);
  const [bulkPolicy, setBulkPolicy] = useState('refundable');
  const [bulkRateTypeEnabled, setBulkRateTypeEnabled] = useState(false);
  const [bulkRateType, setBulkRateType] = useState<'net' | 'gross'>('net');
  const [bulkFinancialEnabled, setBulkFinancialEnabled] = useState(false);
  const [bulkFinancial, setBulkFinancial] = useState('');
  const plans = query.data?.result ?? EMPTY_DRAFT_PLANS;
  const previewGeneration = useRef(0);

  const invalidatePreview = useCallback(() => {
    previewGeneration.current += 1;
    setReviewState((previous) => ({ ...previous, previewRows: {} }));
    setPreviewOpen(false);
  }, []);
  const selectedIds = useMemo(() => Object.keys(selected).filter((id) => selected[id]), [selected]);
  const unavailablePlanIds = useMemo(() => getUnavailableDraftRatePlanIds(rowErrors), [rowErrors]);
  const unavailablePlanIdSet = useMemo(() => new Set(unavailablePlanIds), [unavailablePlanIds]);
  const selectablePlans = useMemo(
    () => plans.filter((plan) => !unavailablePlanIdSet.has(plan.id)),
    [plans, unavailablePlanIdSet],
  );
  // Revisions are canonical fixed-width UTC strings, so lexical order preserves all microseconds.
  const reviewPlansById = useMemo(
    () => reconcileKnownDraftRatePlans(knownPlans, plans, selectedIds, unavailablePlanIds),
    [knownPlans, plans, selectedIds, unavailablePlanIds],
  );
  const changedSelectedUpdates = useMemo(
    () => buildDraftRatePlanUpdates(edits, selectedIds, conflicts, rowErrors),
    [edits, selectedIds, conflicts, rowErrors],
  );
  const approvableSelectedIds = useMemo(
    () => getApprovableDraftRatePlanIds(selectedIds, reviewPlansById, edits, conflicts, rowErrors),
    [selectedIds, reviewPlansById, edits, conflicts, rowErrors],
  );
  const validPreviewCount = Object.values(previewRows).filter((row) => row.ready).length;
  const pageCount = Math.ceil((query.data?.total ?? 0) / filters.limit);
  const visibleSelected = selectablePlans.length > 0 && selectablePlans.every((plan) => selected[plan.id]);

  useEffect(() => {
    setKnownPlans((previous) => reconcileKnownDraftRatePlans(previous, plans, selectedIds, unavailablePlanIds));
    setSelected((previous) => removeUnavailableDraftRatePlanSelections(previous, unavailablePlanIds));
  }, [plans, selectedIds, unavailablePlanIds]);

  useEffect(() => {
    setReviewState((previous) => ({
      ...reconcileDraftRatePlanRefresh(previous, plans),
      previewRows: {},
    }));
  }, [plans]);

  const selectedPlanCodes = approvableSelectedIds.map((id) => reviewPlansById[id]?.rate_plan_code ?? id).join(', ');

  useEffect(() => {
    invalidatePreview();
  }, [chainSfid, filters.page, filters.ratePlanCode, filters.propertySearch, invalidatePreview]);
  const handlePreview = async () => {
    setNotice(null);
    const invalidValueCount = selectedIds.filter((id) => Object.keys(edits[id]?.inputErrors ?? {}).length > 0).length;
    if (changedSelectedUpdates.length === 0) {
      setNotice({
        severity: 'warning',
        text:
          invalidValueCount > 0
            ? 'Correct the selected markup or commission values before reviewing changes.'
            : 'Select edited draft plans to review.',
      });
      return;
    }
    if (changedSelectedUpdates.length > 100) {
      setNotice({ severity: 'warning', text: 'Review up to 100 edited plans per request.' });
      return;
    }
    const generation = ++previewGeneration.current;
    try {
      const response = await previewMutation.mutateAsync({
        chainId: chainSfid,
        updates: changedSelectedUpdates,
      });
      if (generation !== previewGeneration.current) {
        return;
      }
      const requestedIds = new Set(changedSelectedUpdates.map(({ id }) => id));
      const previewCounts = new Map<string, number>();
      response.result.rows.forEach(({ id }) => {
        if (requestedIds.has(id)) {
          previewCounts.set(id, (previewCounts.get(id) ?? 0) + 1);
        }
      });
      const missingPreviewIds = changedSelectedUpdates.map(({ id }) => id).filter((id) => previewCounts.get(id) !== 1);
      const previewRows = Object.fromEntries(
        response.result.rows
          .filter((row) => requestedIds.has(row.id) && previewCounts.get(row.id) === 1)
          .map((row) => [row.id, row]),
      );
      const safePreviewRows = Object.fromEntries(Object.entries(previewRows).filter(([id]) => !conflicts[id]));
      setReviewState((previous) => {
        const rowErrors = { ...previous.rowErrors };
        changedSelectedUpdates.forEach(({ id }) => {
          if (!previous.conflicts[id]) {
            delete rowErrors[id];
          }
        });
        missingPreviewIds.forEach((id) => {
          if (!previous.conflicts[id]) {
            rowErrors[id] = {
              code: 'missing_preview',
              message: 'Preview did not return exactly one row for this draft. Retry the review.',
            };
          }
        });
        const currentPreviewRows = Object.fromEntries(
          Object.entries(safePreviewRows).filter(([id]) => !previous.conflicts[id]),
        );
        return { ...previous, previewRows: currentPreviewRows, rowErrors };
      });
      const notices = [
        invalidValueCount > 0 &&
          `${invalidValueCount} selected row(s) with invalid percentages were excluded; their input is retained.`,
        missingPreviewIds.length > 0 &&
          `Preview was incomplete for ${missingPreviewIds.join(', ')}; retry before saving.`,
      ].filter(Boolean);
      setNotice(notices.length ? { severity: 'warning', text: notices.join(' ') } : null);
      setPreviewOpen(Object.keys(safePreviewRows).length > 0);
    } catch (error) {
      if (generation === previewGeneration.current) {
        setNotice({
          severity: 'error',
          text: requestFailureMessage(error, 'Could not preview the selected draft changes.'),
        });
      }
    }
  };

  const handleSave = async () => {
    const updates = changedSelectedUpdates.filter(
      (update) => previewRows[update.id]?.ready && previewRows[update.id]?.proposed,
    );
    const submittedIds = updates.map(({ id }) => id);
    const expectedUpdatedAtById: Record<string, string> = Object.fromEntries(
      updates.map(({ id, expected_updated_at }) => [id, expected_updated_at]),
    );
    const invalidSelectedCount = selectedIds.filter(
      (id) =>
        Object.keys(edits[id]?.inputErrors ?? {}).length > 0 ||
        (Boolean(edits[id]) && (!previewRows[id]?.ready || !previewRows[id]?.proposed)),
    ).length;
    if (updates.length === 0) {
      setNotice({ severity: 'warning', text: 'Review valid changes before saving.' });
      return;
    }
    try {
      const response = await saveMutation.mutateAsync({ chainId: chainSfid, updates });
      const { succeeded, failed } = response.result;
      const savedById = new Map(succeeded.map((row) => [row.id, row]));
      const successCountById = new Map<string, number>();
      const failureCountById = new Map<string, number>();
      succeeded.forEach(({ id }) => successCountById.set(id, (successCountById.get(id) ?? 0) + 1));
      failed.forEach(({ id }) => failureCountById.set(id, (failureCountById.get(id) ?? 0) + 1));
      const successfulIds = submittedIds.filter((id) => successCountById.get(id) === 1 && !failureCountById.has(id));
      const failedIds = submittedIds.filter(
        (id) => successCountById.get(id) !== 1 || (failureCountById.get(id) ?? 0) !== 0,
      );
      setReviewState((previous) =>
        reconcileDraftRatePlanRefresh(
          applyDraftRatePlanBatchResult(previous, response.result, submittedIds, expectedUpdatedAtById),
          plans,
        ),
      );
      setKnownPlans((previous) => {
        const next = { ...previous };
        successfulIds.forEach((id) => {
          const base = next[id] ?? edits[id]?.plan;
          const proposed = previewRows[id]?.proposed;
          const saved = savedById.get(id);
          if (base && proposed && saved && base.updated_at <= saved.updated_at) {
            next[id] = mergeSavedDraftRatePlan(base, proposed, saved.updated_at);
          }
        });
        return next;
      });
      setSelected((previous) => {
        const next = { ...previous };
        successfulIds.forEach((id) => delete next[id]);
        return next;
      });
      setPreviewOpen(false);
      setConfirmAction(null);
      setNotice({
        severity: failedIds.length || invalidSelectedCount ? 'warning' : 'success',
        text: `${successfulIds.length} draft(s) saved; ${failedIds.length} failed. ${failed
          .filter(({ id }) => submittedIds.includes(id))
          .map(({ id, error }) => `${id} (${error.code}): ${error.message}`)
          .join(' ')}${
          failedIds.some((id) => !failed.some((failure) => failure.id === id))
            ? ` No unique result was returned for ${failedIds.filter((id) => !failed.some((failure) => failure.id === id)).join(', ')}.`
            : ''
        }${
          invalidSelectedCount
            ? ` ${invalidSelectedCount} invalid or unreviewed row(s) were not sent; their edits are retained.`
            : ''
        }`,
      });
    } catch (error) {
      setReviewState((previous) => ({ ...previous, previewRows: {} }));
      setConfirmAction(null);
      setNotice({
        severity: 'error',
        text: `${requestFailureMessage(error, 'Could not save the selected drafts.')} Your edits are retained; review them again before retrying.`,
      });
    }
  };

  const handleApprove = async () => {
    const invalidRowErrors: Record<string, { code: string; message: string }> = {};
    const dirtySelectedCount = selectedIds.filter((id) => Boolean(edits[id])).length;
    const staleSelectedCount = selectedIds.filter((id) => Boolean(conflicts[id] || rowErrors[id])).length;
    selectedIds.forEach((id) => {
      if (edits[id] || conflicts[id] || rowErrors[id]) {
        return;
      }
      const plan = reviewPlansById[id];
      const error = plan ? getDraftApprovalError(plan) : 'Reload the draft before content approval.';
      if (error) {
        invalidRowErrors[id] = { code: 'invalid', message: error };
      }
    });
    const invalidSelectedCount = Object.keys(invalidRowErrors).length;
    if (invalidSelectedCount > 0) {
      setReviewState((previous) => ({
        ...previous,
        rowErrors: { ...previous.rowErrors, ...invalidRowErrors },
      }));
    }
    const plansToApprove = approvableSelectedIds.map((id) => ({
      id,
      expected_updated_at: reviewPlansById[id].updated_at,
    }));
    if (plansToApprove.length === 0) {
      setNotice({
        severity: 'warning',
        text: `${dirtySelectedCount} selected draft(s) have unsaved edits; ${staleSelectedCount} need refresh or recovery; ${invalidSelectedCount} are incomplete or invalid. Only saved, current, valid drafts can be approved.`,
      });
      return;
    }
    if (plansToApprove.length > 100) {
      setNotice({ severity: 'warning', text: 'Approve up to 100 drafts per request.' });
      return;
    }
    const submittedIds = plansToApprove.map(({ id }) => id);
    const expectedUpdatedAtById: Record<string, string> = Object.fromEntries(
      plansToApprove.map(({ id, expected_updated_at }) => [id, expected_updated_at]),
    );
    try {
      const response = await approveMutation.mutateAsync({ chainId: chainSfid, plans: plansToApprove });
      const { succeeded, failed } = response.result;
      const successCountById = new Map<string, number>();
      const failureCountById = new Map<string, number>();
      succeeded.forEach(({ id }) => successCountById.set(id, (successCountById.get(id) ?? 0) + 1));
      failed.forEach(({ id }) => failureCountById.set(id, (failureCountById.get(id) ?? 0) + 1));
      const successfulIds = submittedIds.filter((id) => successCountById.get(id) === 1 && !failureCountById.has(id));
      const failedIds = submittedIds.filter(
        (id) => successCountById.get(id) !== 1 || (failureCountById.get(id) ?? 0) !== 0,
      );
      setReviewState((previous) =>
        reconcileDraftRatePlanRefresh(
          applyDraftRatePlanBatchResult(previous, response.result, submittedIds, expectedUpdatedAtById),
          plans,
        ),
      );
      setSelected((previous) => {
        const next = { ...previous };
        successfulIds.forEach((id) => delete next[id]);
        return next;
      });
      setConfirmAction(null);
      const skippedCount = dirtySelectedCount + invalidSelectedCount + staleSelectedCount;
      setNotice({
        severity: failedIds.length || skippedCount ? 'warning' : 'success',
        text: `${successfulIds.length} plan(s) content-approved; ${failedIds.length} failed. ${failed
          .filter(({ id }) => submittedIds.includes(id))
          .map(({ id, error }) => `${id} (${error.code}): ${error.message}`)
          .join(' ')}${
          failedIds.some((id) => !failed.some((failure) => failure.id === id))
            ? ` No unique result was returned for ${failedIds.filter((id) => !failed.some((failure) => failure.id === id)).join(', ')}.`
            : ''
        }${
          skippedCount
            ? ` ${dirtySelectedCount} with unsaved edits, ${staleSelectedCount} stale or failed, and ${invalidSelectedCount} invalid row(s) were not sent.`
            : ''
        }`,
      });
    } catch (error) {
      setConfirmAction(null);
      setNotice({
        severity: 'error',
        text: requestFailureMessage(error, 'Could not approve the selected drafts.'),
      });
    }
  };

  const applyBulk = () => {
    const changes: ChainDraftRatePlanChanges = {};
    if (bulkBoardEnabled) {
      changes.board_type = bulkBoard;
    }
    if (bulkPolicyEnabled) {
      changes.cancellation_policy = bulkPolicy;
    }
    if (bulkRateTypeEnabled) {
      changes.rate_type = bulkRateType;
    }
    if (bulkFinancialEnabled && !bulkRateTypeEnabled) {
      setNotice({ severity: 'warning', text: 'Enable the common rate type before applying a financial percentage.' });
      return;
    }
    if (bulkFinancialEnabled) {
      const percentage = Number(bulkFinancial);
      if (!bulkFinancial.trim() || !Number.isFinite(percentage) || percentage < 0 || percentage > 100) {
        setNotice({ severity: 'error', text: 'Enter an applicable percentage from 0 to 100.' });
        return;
      }
      if (bulkRateType === 'net') {
        changes.markup = percentage;
      } else {
        changes.commission = percentage;
      }
    }
    if (Object.keys(changes).length === 0 || selectedIds.length === 0) {
      setNotice({ severity: 'warning', text: 'Enable a common field and select draft plans.' });
      return;
    }
    const conflictedSelectedIds = selectedIds.filter(
      (id) => conflicts[id] || isDraftRatePlanRefreshRequired(rowErrors[id]),
    );
    if (conflictedSelectedIds.length > 0) {
      setNotice({
        severity: 'warning',
        text: `Refresh or reload stale drafts before applying common values to ${conflictedSelectedIds.join(', ')}.`,
      });
      return;
    }
    setReviewState((previous) => {
      const rowErrors = { ...previous.rowErrors };
      selectedIds.forEach((id) => delete rowErrors[id]);
      return {
        ...previous,
        edits: applyBulkDraftRatePlanChanges(previous.edits, selectedIds, reviewPlansById, changes),
        previewRows: {},
        rowErrors,
      };
    });
    invalidatePreview();
    setNotice(null);
  };

  const resetSelectedEdits = () => {
    setReviewState((previous) => selectedIds.reduce((state, id) => discardDraftRatePlanEdit(state, id), previous));
    invalidatePreview();
    setNotice(null);
  };

  const editValue = (plan: ChainDraftRatePlan, field: keyof ChainDraftRatePlanChanges) =>
    edits[plan.id]?.changes[field] ?? plan[field];

  const updateField = (plan: ChainDraftRatePlan, field: keyof ChainDraftRatePlanChanges, value: string | number) => {
    setReviewState((previous) => updateDraftReviewEdit(previous, plan, field, value));
    invalidatePreview();
    setNotice(null);
  };
  const updateFinancialField = (plan: ChainDraftRatePlan, field: 'markup' | 'commission', value: string) => {
    setReviewState((previous) => updateDraftReviewFinancialEdit(previous, plan, field, value));
    invalidatePreview();
    setNotice(null);
  };

  const toggleVisible = () => {
    setSelected((previous) => {
      const next = { ...previous };
      selectablePlans.forEach((plan) => {
        if (visibleSelected) {
          delete next[plan.id];
        } else {
          next[plan.id] = true;
        }
      });
      return next;
    });
    invalidatePreview();
  };

  const busy = previewMutation.isPending || saveMutation.isPending || approveMutation.isPending;
  const listError = query.error && !query.isLoading;
  const conflictIds = Object.keys(conflicts);
  const rowErrorIds = Object.keys(rowErrors);

  return (
    <Stack spacing={2}>
      {notice && <Alert severity={notice.severity}>{notice.text}</Alert>}
      {conflictIds.length > 0 && (
        <Alert severity="warning">
          Drafts {conflictIds.join(', ')} changed after editing. Their unsaved changes are preserved but cannot be
          previewed, saved, or approved until discarded and reloaded.
        </Alert>
      )}
      {rowErrorIds.length > 0 && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void query.refetch()} disabled={query.isFetching}>
              Refresh drafts
            </Button>
          }
        >
          Drafts {rowErrorIds.join(', ')} need recovery. Row controls can refresh stale drafts, clear recoverable
          errors, or discard edits.
        </Alert>
      )}
      {listError && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void query.refetch()} disabled={query.isFetching}>
              Retry
            </Button>
          }
        >
          Failed to load individual draft rate plans:{' '}
          {query.error instanceof Error ? query.error.message : 'Unknown error'}
        </Alert>
      )}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1.5}>
          <Typography variant="subtitle2">Apply common values to selected plans</Typography>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
            <FormControlLabel
              control={
                <Checkbox checked={bulkBoardEnabled} onChange={(event) => setBulkBoardEnabled(event.target.checked)} />
              }
              label="Board"
            />
            <FormControl size="small" sx={fieldLabelSx}>
              <InputLabel id="bulk-board-label">Board</InputLabel>
              <Select
                labelId="bulk-board-label"
                label="Board"
                value={bulkBoard}
                onChange={(event) => setBulkBoard(event.target.value)}
                disabled={!bulkBoardEnabled}
              >
                {BOARD_TYPES.map((board) => (
                  <MenuItem key={board} value={board}>
                    {BOARD_TYPE_LABELS[board] ?? board}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={bulkPolicyEnabled}
                  onChange={(event) => setBulkPolicyEnabled(event.target.checked)}
                />
              }
              label="Cancellation policy"
            />
            <FormControl size="small" sx={fieldLabelSx}>
              <InputLabel id="bulk-policy-label">Cancellation</InputLabel>
              <Select
                labelId="bulk-policy-label"
                label="Cancellation"
                value={bulkPolicy}
                onChange={(event) => setBulkPolicy(event.target.value)}
                disabled={!bulkPolicyEnabled}
              >
                {CANCELLATION_POLICY_OPTIONS.map((policy) => (
                  <MenuItem key={policy} value={policy}>
                    {policy}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={bulkRateTypeEnabled}
                  onChange={(event) => setBulkRateTypeEnabled(event.target.checked)}
                />
              }
              label="Rate type"
            />
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <InputLabel id="bulk-rate-type-label">Rate type</InputLabel>
              <Select
                labelId="bulk-rate-type-label"
                label="Rate type"
                value={bulkRateType}
                onChange={(event) => setBulkRateType(event.target.value)}
                disabled={!bulkRateTypeEnabled}
              >
                <MenuItem value="net">Net</MenuItem>
                <MenuItem value="gross">Gross</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              disabled={!bulkRateTypeEnabled}
              control={
                <Checkbox
                  checked={bulkFinancialEnabled}
                  onChange={(event) => setBulkFinancialEnabled(event.target.checked)}
                />
              }
              label={bulkRateType === 'net' ? 'Markup %' : 'Commission %'}
            />
            <TextField
              size="small"
              type="number"
              label={bulkRateType === 'net' ? 'Markup %' : 'Commission %'}
              value={bulkFinancial}
              onChange={(event) => setBulkFinancial(event.target.value)}
              inputProps={{ min: 0, max: 100, step: 0.01 }}
              disabled={!bulkFinancialEnabled || !bulkRateTypeEnabled}
              sx={{ width: 130 }}
            />
            <Button variant="outlined" size="small" onClick={applyBulk} disabled={busy || selectedIds.length === 0}>
              Apply enabled values
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Button variant="outlined" onClick={handlePreview} disabled={busy || changedSelectedUpdates.length === 0}>
          Review changes
        </Button>
        <Button
          variant="contained"
          onClick={() => setConfirmAction('save')}
          disabled={
            busy || validPreviewCount === 0 || !changedSelectedUpdates.some((update) => previewRows[update.id]?.ready)
          }
        >
          Save selected drafts
        </Button>
        <Button
          variant="outlined"
          color="success"
          onClick={() => setConfirmAction('approve')}
          disabled={busy || approvableSelectedIds.length === 0}
        >
          Approve selected
        </Button>
        <Button
          variant="text"
          onClick={resetSelectedEdits}
          disabled={
            busy ||
            selectedIds.some((id) => Boolean(conflicts[id])) ||
            !selectedIds.some((id) => Boolean(edits[id] || rowErrors[id]))
          }
        >
          Discard selected edits / clear recoverable errors
        </Button>
        {(previewMutation.isPending || saveMutation.isPending || approveMutation.isPending) && (
          <CircularProgress size={22} />
        )}
        <Typography variant="body2" color="text.secondary">
          {selectedIds.length} selected · {Object.keys(edits).length} edited
        </Typography>
      </Stack>

      {query.isLoading && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      )}
      {!query.isLoading && !listError && plans.length === 0 && (
        <Box textAlign="center" py={6} color="text.secondary">
          <Typography>No individual draft rate plans match these filters.</Typography>
        </Box>
      )}
      {plans.length > 0 && (
        <Paper variant="outlined" sx={{ overflow: 'auto' }}>
          <Table size="small" sx={{ minWidth: 1450 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={visibleSelected}
                    indeterminate={selectablePlans.some((plan) => selected[plan.id]) && !visibleSelected}
                    disabled={busy || selectablePlans.length === 0}
                    onChange={toggleVisible}
                    inputProps={{ 'aria-label': 'Select visible draft plans' }}
                  />
                </TableCell>
                <TableCell>Properties / vendor</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Board</TableCell>
                <TableCell>Cancellation</TableCell>
                <TableCell>Rate type</TableCell>
                <TableCell>Markup / commission</TableCell>
                <TableCell>State</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {plans.map((plan) => {
                const rateType = editValue(plan, 'rate_type') as 'net' | 'gross';
                const financialField = rateType === 'net' ? 'markup' : 'commission';
                const changed = Boolean(edits[plan.id]);
                const conflict = conflicts[plan.id];
                const rowDisabled = busy || Boolean(conflict) || isDraftRatePlanRefreshRequired(rowErrors[plan.id]);
                const approvalError = changed ? null : getDraftApprovalError(plan);
                const financialInputValue =
                  edits[plan.id]?.inputValues?.[financialField] ?? editValue(plan, financialField) ?? '';
                const financialInputError = edits[plan.id]?.inputErrors?.[financialField];
                return (
                  <TableRow key={plan.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={Boolean(selected[plan.id])}
                        disabled={busy || unavailablePlanIdSet.has(plan.id)}
                        onChange={(event) => {
                          setSelected((previous) => {
                            if (event.target.checked === Boolean(previous[plan.id])) {
                              return previous;
                            }
                            const next = { ...previous };
                            if (event.target.checked) {
                              next[plan.id] = true;
                            } else {
                              delete next[plan.id];
                            }
                            return next;
                          });
                          invalidatePreview();
                        }}
                        inputProps={{ 'aria-label': `Select ${plan.rate_plan_code ?? plan.id}` }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {plan.property_names.join(', ')}
                        <br />
                        {plan.vendor_id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography component="code">{plan.rate_plan_code ?? '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={editValue(plan, 'name') ?? ''}
                        onChange={(event) => updateField(plan, 'name', event.target.value)}
                        disabled={rowDisabled}
                        inputProps={{ 'aria-label': `Name ${plan.id}` }}
                      />
                    </TableCell>
                    <TableCell>
                      <Select
                        size="small"
                        value={editValue(plan, 'board_type')}
                        onChange={(event) => updateField(plan, 'board_type', event.target.value)}
                        disabled={rowDisabled}
                        inputProps={{ 'aria-label': `Board ${plan.id}` }}
                      >
                        {BOARD_TYPES.map((board) => (
                          <MenuItem key={board} value={board}>
                            {BOARD_TYPE_LABELS[board] ?? board}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Select
                        size="small"
                        value={editValue(plan, 'cancellation_policy') ?? ''}
                        onChange={(event) => updateField(plan, 'cancellation_policy', event.target.value)}
                        disabled={rowDisabled}
                        inputProps={{ 'aria-label': `Cancellation ${plan.id}` }}
                      >
                        {CANCELLATION_POLICY_OPTIONS.map((policy) => (
                          <MenuItem key={policy} value={policy}>
                            {policy}
                          </MenuItem>
                        ))}
                        {plan.cancellation_policy === null && editValue(plan, 'cancellation_policy') === null && (
                          <MenuItem value="">Select policy…</MenuItem>
                        )}
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Select
                        size="small"
                        value={rateType}
                        onChange={(event) => updateField(plan, 'rate_type', event.target.value)}
                        disabled={rowDisabled}
                        inputProps={{ 'aria-label': `Rate type ${plan.id}` }}
                      >
                        <MenuItem value="net">Net</MenuItem>
                        <MenuItem value="gross">Gross</MenuItem>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={financialInputValue}
                        onChange={(event) => updateFinancialField(plan, financialField, event.target.value)}
                        disabled={rowDisabled}
                        error={Boolean(financialInputError)}
                        helperText={financialInputError}
                        inputProps={{ min: 0, max: 100, step: 0.01, 'aria-label': `${financialField} ${plan.id}` }}
                        sx={{ width: 145 }}
                      />
                      <Typography variant="caption" sx={{ ml: 0.5 }}>
                        {financialField === 'markup' ? 'markup' : 'commission'}%
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {conflict ? (
                        <Typography color="error.main">Stale</Typography>
                      ) : changed ? (
                        <Typography color="warning.main">Unsaved</Typography>
                      ) : (
                        <Typography color="text.secondary">Draft</Typography>
                      )}
                      {changed && !conflict && (
                        <Alert severity="warning" icon={false} sx={{ mt: 0.5, py: 0 }}>
                          Save or reset edits before approval.
                        </Alert>
                      )}
                      {conflict && (
                        <Alert
                          severity="warning"
                          sx={{ mt: 0.5 }}
                          action={
                            <Button
                              color="inherit"
                              size="small"
                              onClick={() => {
                                setKnownPlans((previous) => ({ ...previous, [plan.id]: conflict.latest }));
                                setReviewState((previous) => restoreLatestDraftRatePlan(previous, plan.id));
                                invalidatePreview();
                                setNotice({
                                  severity: 'warning',
                                  text: `Unsaved changes for ${plan.rate_plan_code ?? plan.id} were discarded; current values are loaded.`,
                                });
                              }}
                            >
                              Use latest
                            </Button>
                          }
                        >
                          This draft changed at {conflict.latest.updated_at}. Use the latest revision to discard unsaved
                          values and continue.
                        </Alert>
                      )}
                      {rowErrors[plan.id] && (
                        <Alert
                          severity="error"
                          sx={{ mt: 0.5 }}
                          action={
                            <Button
                              color="inherit"
                              size="small"
                              disabled={busy}
                              onClick={() => {
                                if (isDraftRatePlanRefreshRequired(rowErrors[plan.id])) {
                                  void query.refetch();
                                  return;
                                }
                                setReviewState((previous) => clearDraftRatePlanRowError(previous, plan.id));
                              }}
                            >
                              {isDraftRatePlanRefreshRequired(rowErrors[plan.id]) ? 'Refresh' : 'Clear'}
                            </Button>
                          }
                        >
                          {rowErrors[plan.id].code}: {rowErrors[plan.id].message}
                        </Alert>
                      )}
                      {previewRows[plan.id]?.errors.map((error, index) => (
                        <Alert key={`${error.code}-${index}`} severity="error" sx={{ mt: 0.5 }}>
                          {error.message}
                        </Alert>
                      ))}
                      {approvalError && (
                        <Alert severity="warning" sx={{ mt: 0.5 }}>
                          {approvalError}
                        </Alert>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}
      {pageCount > 1 && (
        <Box display="flex" justifyContent="center">
          <Pagination
            count={pageCount}
            page={filters.page}
            onChange={(_, page) => onPageChange(page)}
            color="primary"
          />
        </Box>
      )}

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Review draft changes</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            {Object.entries(previewRows).map(([id, row]) => (
              <Paper key={id} variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="subtitle2">
                  {row.current?.rate_plan_code ?? id} · {row.current?.name ?? 'Unavailable'}
                </Typography>
                {row.errors.map((error) => (
                  <Alert key={`${id}-${error.code}`} severity="error">
                    {error.message}
                  </Alert>
                ))}
                {row.current && row.proposed && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Name: {row.current.name} → {row.proposed.name}; board: {row.current.board_type} →{' '}
                    {row.proposed.board_type}; cancellation: {row.current.cancellation_policy} →{' '}
                    {row.proposed.cancellation_policy}; rate type: {row.current.rate_type} → {row.proposed.rate_type};
                    markup: {row.current.markup} → {row.proposed.markup}; commission: {row.current.commission} →{' '}
                    {row.proposed.commission}
                  </Typography>
                )}
              </Paper>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => {
              setPreviewOpen(false);
              setConfirmAction('save');
            }}
            disabled={validPreviewCount === 0}
          >
            Save {validPreviewCount} valid drafts
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmAction !== null} onClose={() => setConfirmAction(null)}>
        <DialogTitle>{confirmAction === 'approve' ? 'Approve selected drafts?' : 'Save selected drafts?'}</DialogTitle>
        <DialogContent>
          <Typography>
            {confirmAction === 'approve'
              ? `Content-approve ${approvableSelectedIds.length} saved, valid draft(s): ${selectedPlanCodes}?`
              : `Save ${changedSelectedUpdates.filter((update) => previewRows[update.id]?.ready).length} draft(s) without approving them?`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAction(null)}>Cancel</Button>
          <Button
            variant="contained"
            color={confirmAction === 'approve' ? 'success' : 'primary'}
            onClick={confirmAction === 'approve' ? handleApprove : handleSave}
            disabled={busy}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
